import { Container, Graphics } from "pixi.js";
import {
  type ChunkData,
  type ChunkKey,
  CHUNK_SIZE,
  chunkKey,
  TILE_H,
  TILE_W,
  type TerrainId,
} from "@vinyl/shared";
import { tileToScreen } from "./iso.js";

const COLORS: Record<TerrainId, number> = {
  grass: 0x3f6e3a,
  stone: 0x6b6b6b,
  wood: 0x8c5a2b,
  ice: 0x9be8e8,
  water: 0x2c4c8a,
  // Warm tan, brighter than wood so paths are visually distinct from
  // wooden floors / planks. Walkable terrain (see NON_BLOCKING in zones.ts).
  path: 0xb8895f,
};

// M11 — chunked tilemap renderer. The full zone is no longer rendered up
// front; instead, each chunk that arrives via chunkData is built as its
// own Graphics and added to the container. Chunks the client never
// receives (because the player never approached them) cost zero render
// time. Replaces the legacy buildTilemap(map: TilemapJson): Container.
export class ChunkedTilemap {
  readonly container = new Container();
  private readonly chunkGraphics = new Map<ChunkKey, Graphics>();

  constructor() {
    this.container.label = "tilemap";
  }

  // Drop every loaded chunk. Called on zone change.
  clear(): void {
    this.container.removeChildren();
    for (const g of this.chunkGraphics.values()) g.destroy();
    this.chunkGraphics.clear();
  }

  // Add (or replace if already present) a chunk's terrain visuals.
  // Idempotent: re-ingesting a chunk replaces the previous Graphics.
  ingest(chunk: ChunkData): void {
    const key = chunkKey(chunk.cx, chunk.cy);
    const old = this.chunkGraphics.get(key);
    if (old) {
      this.container.removeChild(old);
      old.destroy();
      this.chunkGraphics.delete(key);
    }
    const g = buildChunkGraphics(chunk);
    this.chunkGraphics.set(key, g);
    this.container.addChild(g);
  }

  destroy(): void {
    this.clear();
    this.container.destroy({ children: true });
  }
}

function buildChunkGraphics(chunk: ChunkData): Graphics {
  const g = new Graphics();
  const x0 = chunk.cx * CHUNK_SIZE;
  const y0 = chunk.cy * CHUNK_SIZE;
  for (let dy = 0; dy < chunk.height; dy++) {
    for (let dx = 0; dx < chunk.width; dx++) {
      const tx = x0 + dx;
      const ty = y0 + dy;
      const terrain = chunk.terrain[dy * chunk.width + dx] ?? "grass";
      const fill = COLORS[terrain];
      const { sx, sy } = tileToScreen(tx, ty);
      g.poly([
        sx,
        sy - TILE_H / 2,
        sx + TILE_W / 2,
        sy,
        sx,
        sy + TILE_H / 2,
        sx - TILE_W / 2,
        sy,
      ])
        .fill({ color: fill })
        .stroke({ color: 0x000000, alpha: 0.18, width: 1 });
    }
  }
  return g;
}

export function highlightTile(target: Container, tx: number, ty: number): void {
  target.removeChildren();
  const { sx, sy } = tileToScreen(tx, ty);
  const g = new Graphics()
    .poly([
      sx,
      sy - TILE_H / 2,
      sx + TILE_W / 2,
      sy,
      sx,
      sy + TILE_H / 2,
      sx - TILE_W / 2,
      sy,
    ])
    .stroke({ color: 0xd4af37, alpha: 0.85, width: 2 });
  target.addChild(g);
}
